/**
 * Banner Resize Module
 * Resizes 300x600 banners responsively based on screen size
 * Breakpoints match page-content.sass:
 * - > 1499px: 300x600 (original)
 * - 992px - 1499px: 230x460 (large)
 * - 768px - 991px: 170x340 (medium)
 * - < 768px: 130x260 (small)
 */

/**
 * Get target banner dimensions based on viewport width
 */
function getTargetDimensions() {
	const width = window.innerWidth;

	if (width > 1499) {
		return { width: 300, height: 600 };
	} else if (width > 991) {
		return { width: 230, height: 460 };
	} else if (width > 767) {
		return { width: 170, height: 340 };
	} else {
		return { width: 130, height: 260 };
	}
}

const SIDEBAR_ORIGINAL_WIDTH = 300;
const SIDEBAR_ORIGINAL_HEIGHT = 600;

export function resizeSidebarBanners() {
	const target = getTargetDimensions();
	const zoomLevel = target.width / SIDEBAR_ORIGINAL_WIDTH;

	document.querySelectorAll('.sidebar-banner').forEach(container => {
		const children = container.querySelectorAll(':scope > :not(script):not(style)');

		children.forEach(child => {
			if (zoomLevel >= 0.99) {
				child.style.transform = '';
				if (child.tagName === 'IFRAME') {
					child.style.removeProperty('width');
					child.style.removeProperty('height');
					child.style.removeProperty('max-width');
					child.style.removeProperty('max-height');
				}
				return;
			}

			if (child.tagName === 'IFRAME') {
				const src = child.getAttribute('src') || '';
				const w = parseInt(child.getAttribute('width')) || 0;
				if (src === 'about:blank' && w <= 10) return;

				child.style.setProperty('width', SIDEBAR_ORIGINAL_WIDTH + 'px', 'important');
				child.style.setProperty('height', SIDEBAR_ORIGINAL_HEIGHT + 'px', 'important');
				child.style.setProperty('max-width', 'none', 'important');
				child.style.setProperty('max-height', 'none', 'important');
			} else {
				const childWidth = child.scrollWidth || child.offsetWidth;
				if (childWidth <= target.width + 5) return;
			}

			child.style.transform = `scale(${zoomLevel})`;
			child.style.transformOrigin = 'top left';
		});
	});
}

/**
 * Get horizontal banner dimensions based on viewport width (728x90 format)
 * Breakpoints match banner.sass lines 75-110
 */
function getHorizontalBannerDimensions() {
	const width = window.innerWidth;

	if (width > 1299) {
		return { width: 728, height: 113.75 };
	} else if (width > 1199) {
		return { width: 600, height: 93.75 };
	} else if (width > 1099) {
		return { width: 544, height: 85 };
	} else if (width > 991) {
		return { width: 442, height: 69.06 };
	} else if (width > 850) {
		return { width: 430, height: 67.19 };
	} else if (width > 670) {
		return { width: 348, height: 54.38 };
	} else if (width > 575) {
		return { width: 256, height: 40 };
	} else if (width > 500) {
		return { width: 470, height: 73.44 };
	} else if (width > 430) {
		return { width: 400, height: 62.5 };
	} else if (width > 370) {
		return { width: 340, height: 53.13 };
	} else {
		return { width: 290, height: 45.31 };
	}
}

/**
 * Get square banner dimensions based on viewport width (300x250 format)
 * Breakpoints match banner.sass lines 112-125
 */
function getSquareBannerDimensions() {
	const width = window.innerWidth;

	if (width > 670) {
		return { width: 300, height: 250 };
	} else if (width > 575) {
		return { width: 256, height: 213 };
	} else if (width > 340) {
		return { width: 300, height: 250 };
	} else {
		return { width: 290, height: 241 };
	}
}

/**
 * Get midscroll banner dimensions based on viewport width (728x410 native format, 728:410 aspect ratio)
 * Breakpoints match banner.sass midscroll section
 */
function getMidscrollBannerDimensions() {
	const width = window.innerWidth;

	if (width > 1299) {
		return { width: 728, height: 410 };
	} else if (width > 1199) {
		return { width: 600, height: 337.91 };
	} else if (width > 1099) {
		return { width: 544, height: 306.59 };
	} else if (width > 991) {
		return { width: 442, height: 248.93 };
	} else if (width > 850) {
		return { width: 430, height: 242.17 };
	} else if (width > 670) {
		return { width: 348, height: 196.04 };
	} else if (width > 575) {
		return { width: 256, height: 144.18 };
	} else if (width > 500) {
		return { width: 470, height: 264.73 };
	} else if (width > 430) {
		return { width: 400, height: 225.27 };
	} else if (width > 370) {
		return { width: 340, height: 191.48 };
	} else {
		return { width: 290, height: 163.35 };
	}
}


/**
 * Resize content banners in .wp-block-ligamanager-banner blocks
 * Handles horizontal (320x50), square (300x250), and midscroll (728x410, 930x180, 930x600) formats
 */
export function resizeContentBanners() {
	const bannerBlocks = document.querySelectorAll('.wp-block-ligamanager-banner');

	bannerBlocks.forEach(block => {
		const isSquare = block.classList.contains('banner-square');
		const midscrollVariant = block.classList.contains('banner-midscroll-930x180') ? '930x180'
			: block.classList.contains('banner-midscroll-930x600') ? '930x600'
			: block.classList.contains('banner-midscroll') ? '728x410'
			: null;
		const isMidscroll = midscrollVariant !== null;
		const isHorizontal = !isSquare && !isMidscroll;

		const bannerContainer = block.querySelector('.banner');
		if (!bannerContainer) return;

		if (isHorizontal) {
			const target = getHorizontalBannerDimensions();

			bannerContainer.querySelectorAll(':scope > :not(script):not(style)').forEach(child => {
				let origW, origH;

				if (child.tagName === 'IFRAME') {
					origW = parseInt(child.getAttribute('width')) || 0;
					origH = parseInt(child.getAttribute('height')) || 0;
				} else {
					origW = parseInt(child.style.width) || child.offsetWidth;
					origH = parseInt(child.style.height) || child.offsetHeight;
				}
				if (!origW || !origH) return;

				const scaleLevel = target.width / origW;
				const scaledH = origH * scaleLevel;

				if (Math.abs(scaleLevel - 1) < 0.01) {
					child.style.transform = '';
					if (child.tagName === 'IFRAME') {
						child.style.removeProperty('width');
						child.style.removeProperty('height');
						child.style.removeProperty('max-width');
						child.style.removeProperty('max-height');
					}
				} else {
					if (child.tagName === 'IFRAME') {
						const src = child.getAttribute('src') || '';
						const w = parseInt(child.getAttribute('width')) || 0;
						if (src === 'about:blank' && w <= 10) return;

						child.style.setProperty('width', origW + 'px', 'important');
						child.style.setProperty('height', origH + 'px', 'important');
						child.style.setProperty('max-width', 'none', 'important');
						child.style.setProperty('max-height', 'none', 'important');
					}

					child.style.transform = `scale(${scaleLevel})`;
					child.style.transformOrigin = 'top left';
				}

				bannerContainer.style.height = scaledH + 'px';
				bannerContainer.style.overflow = 'hidden';
			});
			return;
		}

		if (isMidscroll && midscrollVariant === '728x410') {
			const target = getMidscrollBannerDimensions();
			const NATIVE_W = 728;
			const NATIVE_H = 410;
			const scaleLevel = target.width / NATIVE_W;
			const scaledH = NATIVE_H * scaleLevel;

			bannerContainer.querySelectorAll('iframe').forEach(iframe => {
				const src = iframe.getAttribute('src') || '';
				const w = parseInt(iframe.getAttribute('width')) || 0;
				if (src === 'about:blank' && w <= 10) return;

				if (Math.abs(scaleLevel - 1) < 0.01) {
					iframe.style.transform = '';
					iframe.style.removeProperty('width');
					iframe.style.removeProperty('height');
					iframe.style.removeProperty('max-width');
					iframe.style.removeProperty('max-height');
				} else {
					iframe.style.setProperty('width', NATIVE_W + 'px', 'important');
					iframe.style.setProperty('height', NATIVE_H + 'px', 'important');
					iframe.style.setProperty('max-width', 'none', 'important');
					iframe.style.setProperty('max-height', 'none', 'important');
					iframe.style.transform = `scale(${scaleLevel})`;
					iframe.style.transformOrigin = 'top left';
				}
			});

			bannerContainer.style.height = scaledH + 'px';
			bannerContainer.style.overflow = 'hidden';
			return;
		}

		if (isMidscroll) {
			// 930x180 / 930x600: ad networks may inject an <iframe> or a plain
			// element directly (e.g. Adform's .adform-adbox div), so scale
			// whichever direct child is actually rendered, like the horizontal branch does.
			//
			// .banner itself is fluid (width:100%; max-width:930px; aspect-ratio in CSS),
			// not stepped at fixed viewport breakpoints — the surrounding sidebar/grid
			// column doesn't shrink at round numbers either, so a hardcoded lookup table
			// drifts out of sync with the real column width and the banner overflows it.
			// Reading the container's actual rendered width avoids that entirely.
			const NATIVE_W = 930;
			const NATIVE_H = midscrollVariant === '930x600' ? 600 : 180;
			const containerWidth = bannerContainer.getBoundingClientRect().width;
			if (!containerWidth) return;
			const scaleLevel = containerWidth / NATIVE_W;

			bannerContainer.querySelectorAll(':scope > :not(script):not(style)').forEach(child => {
				if (child.tagName === 'IFRAME') {
					const src = child.getAttribute('src') || '';
					const w = parseInt(child.getAttribute('width')) || 0;
					if (src === 'about:blank' && w <= 10) return;
				}

				// The global ".banner > *{width:100% !important;height:100% !important}"
				// rule (banner.sass) applies to any direct child, iframe or not. Left
				// alone, it forces the child to already fill the (scaled-down) container
				// before our transform runs, so the transform then shrinks it a second
				// time. Re-asserting the native size as an inline !important (which wins
				// over that external !important rule) is required for every tag, not just
				// IFRAME, so the transform scales down from the true native size.
				if (Math.abs(scaleLevel - 1) < 0.01) {
					child.style.transform = '';
					child.style.removeProperty('width');
					child.style.removeProperty('height');
					child.style.removeProperty('max-width');
					child.style.removeProperty('max-height');
				} else {
					child.style.setProperty('width', NATIVE_W + 'px', 'important');
					child.style.setProperty('height', NATIVE_H + 'px', 'important');
					child.style.setProperty('max-width', 'none', 'important');
					child.style.setProperty('max-height', 'none', 'important');
					child.style.transform = `scale(${scaleLevel})`;
					child.style.transformOrigin = 'top left';
				}
			});

			// Height is handled by CSS aspect-ratio on .banner, not set here.
			return;
		}

		// Square: use processDocument() for same-origin iframes
		const targetDimensions = getSquareBannerDimensions();
		const originalDimensions = { width: 300, height: 250 };
		const targetWidth = targetDimensions.width;
		const targetHeight = targetDimensions.height;

		function processDocument(doc) {
			if (!doc) return;

			const attrSelector = `[width="${originalDimensions.width}"][height="${originalDimensions.height}"]`;
			const attrElements = doc.querySelectorAll(attrSelector);
			attrElements.forEach(el => {
				el.setAttribute('width', targetWidth.toString());
				el.setAttribute('height', targetHeight.toString());
			});

			const all = doc.querySelectorAll('*');
			all.forEach(el => {
				const style = el.style;
				const matchesWidth = style.width === `${originalDimensions.width}px` || style.width === originalDimensions.width.toString();
				const matchesHeight = style.height === `${originalDimensions.height}px` || style.height === originalDimensions.height.toString();
				if (matchesWidth && matchesHeight) {
					style.width = targetWidth + 'px';
					style.height = targetHeight + 'px';
				}
			});

			const frames = doc.querySelectorAll('iframe');
			frames.forEach(frame => {
				try {
					const innerDoc = frame.contentDocument || frame.contentWindow?.document;
					processDocument(innerDoc);
				} catch (e) {
					// Cross-origin iframe – skip it
				}
			});
		}

		const iframes = bannerContainer.querySelectorAll('iframe');
		iframes.forEach(frame => {
			try {
				const doc = frame.contentDocument || frame.contentWindow?.document;
				processDocument(doc);
			} catch (e) {
				// Cross-origin iframe – skip it
			}
		});
	});
}

// /**
//  * Initialize parallax effect for midscroll banners
//  */
// function initMidscrollParallax() {
// 	const midscrollBanners = document.querySelectorAll('.banner-midscroll .banner');

// 	if (midscrollBanners.length === 0) return;

// 	function updateParallax() {
// 		midscrollBanners.forEach(banner => {
// 			const container = banner.closest('.banner-midscroll');
// 			if (!container) return;

// 			const rect = container.getBoundingClientRect();
// 			const scrollProgress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);

// 			// Only apply parallax when banner is in viewport
// 			if (scrollProgress >= 0 && scrollProgress <= 1) {
// 				// Parallax effect: move in opposite direction (reversed scroll)
// 				const parallaxOffset = -(scrollProgress - 0.5) * 100;
// 				banner.style.transform = `translateX(-50%) translateY(${parallaxOffset}px)`;
// 			}
// 		});
// 	}

// 	// Update on scroll with throttling for performance
// 	let ticking = false;
// 	window.addEventListener('scroll', () => {
// 		if (!ticking) {
// 			window.requestAnimationFrame(() => {
// 				updateParallax();
// 				ticking = false;
// 			});
// 			ticking = true;
// 		}
// 	});

// 	// Initial update
// 	updateParallax();

// 	// Update on resize
// 	window.addEventListener('resize', updateParallax);
// }

/**
 * Initialize content banner resize functionality
 * Observes banner blocks for loads and processes them
 */
export function initContentBannerResize() {
	// Initial resize for any existing banners
	const resizeBanners = () => {
		// Wait a bit for iframes to load
		setTimeout(() => {
			resizeContentBanners();
		}, 100);
	};

	// Watch for banner block content changes
	const observer = new MutationObserver((mutations) => {
		mutations.forEach((mutation) => {
			mutation.addedNodes.forEach((node) => {
				if (node.nodeType === 1) { // Element node
					// Check if this node or its children contain wp-block-ligamanager-banner
					if (node.classList?.contains('wp-block-ligamanager-banner') ||
						node.querySelector?.('.wp-block-ligamanager-banner')) {
						resizeBanners();
						// // Reinitialize parallax if new midscroll banners are added
						// if (node.classList?.contains('banner-midscroll') ||
						// 	node.querySelector?.('.banner-midscroll')) {
						// 	setTimeout(initMidscrollParallax, 200);
						// }
					}
				}
			});
		});
	});

	// Start observing the document body for banner block changes
	observer.observe(document.body, {
		childList: true,
		subtree: true
	});

	// Also resize on iframe load events
	document.addEventListener('load', (e) => {
		if (e.target.tagName === 'IFRAME') {
			setTimeout(() => {
				resizeContentBanners();
			}, 100);
		}
	}, true);

	// Debounce function for window resize
	let resizeTimeout;
	const handleResize = () => {
		clearTimeout(resizeTimeout);
		resizeTimeout = setTimeout(() => {
			resizeContentBanners();
		}, 250);
	};

	// Handle window resize to adjust banners for different breakpoints
	window.addEventListener('resize', handleResize);

	// Run initial resize
	resizeBanners();

	// Re-run periodically to catch late-loading banners
	const interval = setInterval(resizeBanners, 2000);

	// Clean up after 30 seconds (banners should be loaded by then)
	setTimeout(() => {
		clearInterval(interval);
	}, 30000);

	// Initialize parallax effect for midscroll banners
	// initMidscrollParallax();
}

/**
 * Initialize banner resize functionality
 * Observes sidebar for banner loads and processes them
 */
export function initBannerResize() {
	// Initial resize for any existing banners
	const resizeBanners = () => {
		// Wait a bit for iframes to load
		setTimeout(() => {
			resizeSidebarBanners();
		}, 100);
	};

	// Watch for sidebar content changes (when AJAX loaded)
	const observer = new MutationObserver((mutations) => {
		mutations.forEach((mutation) => {
			mutation.addedNodes.forEach((node) => {
				if (node.nodeType === 1) { // Element node
					// Check if this node or its children contain sidebar-banner
					if (node.classList?.contains('sidebar-banner') ||
						node.querySelector?.('.sidebar-banner')) {
						resizeBanners();
					}
				}
			});
		});
	});

	// Start observing the document body for sidebar changes
	observer.observe(document.body, {
		childList: true,
		subtree: true
	});

	// Also resize on iframe load events
	document.addEventListener('load', (e) => {
		if (e.target.tagName === 'IFRAME') {
			setTimeout(() => {
				resizeSidebarBanners();
			}, 100);
		}
	}, true);

	// Debounce function for window resize
	let resizeTimeout;
	const handleResize = () => {
		clearTimeout(resizeTimeout);
		resizeTimeout = setTimeout(() => {
			resizeSidebarBanners();
		}, 250);
	};

	// Handle window resize to adjust banners for different breakpoints
	window.addEventListener('resize', handleResize);

	// Run initial resize
	resizeBanners();

	// Re-run periodically to catch late-loading banners
	const interval = setInterval(resizeBanners, 2000);

	// Clean up after 30 seconds (banners should be loaded by then)
	setTimeout(() => {
		clearInterval(interval);
	}, 30000);
}

